import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Game, type League } from './api';
import { reloadNow, subscribe } from './liveScores';

/**
 * This week's games for one league, kept fresh by the shared scores feed.
 *
 * Every component asking for the same league shares one request and one timer,
 * so the four copies of this hook on the home page cost what one costs. See
 * liveScores.ts for why this polls rather than holding a socket open.
 */
export function useGames(league: League = 'nfl') {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    let live = true;
    let arrived = false;

    const stop = subscribe(league, (next) => {
      if (!live) return;
      arrived = true;
      setGames(next);
      setLoading(false);
      setError(null);
    });

    // The feed keeps the last good scores rather than throwing, so the only
    // failure worth showing is having nothing at all after a fair wait.
    const giveUp = window.setTimeout(() => {
      if (live && !arrived) {
        setLoading(false);
        setError('Could not load the scoreboard.');
      }
    }, 15_000);

    return () => {
      live = false;
      window.clearTimeout(giveUp);
      stop();
    };
  }, [league]);

  const reload = useCallback(() => {
    setError(null);
    setLoading(true);
    void reloadNow(league).finally(() => setLoading(false));
  }, [league]);

  return { games, loading, error, reload };
}

/**
 * Both leagues, for pages that look a game up by id.
 *
 * The array has to be memoised. Spreading the two leagues on every render handed
 * every caller a new array identity each time, so a `useMemo` keyed on it never
 * held and a `useEffect` depending on it fired on every render. On the home page
 * that effect requests live grades and then sets state, which re-rendered, which
 * built another new array, which fired it again: a loop bounded only by network
 * latency. It ran up 55,535 live-grade requests before anyone noticed, because
 * nothing about it looks wrong and no error is ever thrown.
 */
export function useAllGames() {
  const nfl = useGames('nfl');
  const cfb = useGames('cfb');
  const games = useMemo(() => [...nfl.games, ...cfb.games], [nfl.games, cfb.games]);
  return { games, loading: nfl.loading || cfb.loading };
}
